import type {
  AccountView,
  BundleView,
  CardView,
  DataFlag,
  LedgerEntry,
  MandateView,
  OccurrenceView,
  Receipt,
  ReceiptStep,
  SignedStep,
  StepBalance,
} from "../api/types.js";
import { clock, dateTime, formatMoney, fullWhen, lastDigits, proxyTail, sgDay, shareCount, shortWhen, statementDate } from "../lib/format.js";
import { proxyLabel } from "./add-payee-model.js";
import { NOTHING_LEFT, doneOf, nothingWent as nothingRan } from "./card-end.js";
import { debitSource, describeStep, shownAs, sourceShown, type Inert, type Row } from "./describe.js";
import { inertNotices, type InertNotice } from "./inert.js";
import { heldReason } from "./notices.js";
import { fullDate, mandateDoneTitle, mandateFrom, mandateName, paymentAmount, ruleLine, shortDate } from "./schedule-words.js";
import type { Delta } from "./success-model.js";

/**
 * A receipt (UX spec v3 §6.13, copy §8.2): the success sheet's parts, read back
 * later, for one operation of a signed bundle or one ledger line (DCTA-166: a refused
 * draft is no longer in the customer's history, so it has no receipt here — the bank's
 * audit trail keeps it).
 *
 *  - WHAT and WHO come from the signed payload (the words the customer signed);
 *  - WHAT RAN from the receipt;
 *  - BEFORE → AFTER from the receipt's per-operation balances (ask e) — the
 *    ledger's own running balance at that posting; no figure is computed here;
 *  - a ledger line the assistant did not make is printed as the bank wrote it,
 *    its reference verbatim (it may carry an injection: it is text).
 * Verified is offered only where there is a signature to check; a ledger line from
 * elsewhere has none.
 */
/** A signed bundle as two reads tell it: the payload (`/v1/bundles/{id}`) and what ran (`/v1/gateway/bundles/{id}`). */
export interface BundleRead {
  readonly view: BundleView;
  readonly receipt: Receipt;
}

export interface ReceiptContext {
  readonly bundles: ReadonlyMap<string, BundleRead>;
  readonly entries: readonly LedgerEntry[];
  readonly accounts: readonly AccountView[];
  readonly cards: readonly CardView[];
}

export interface KV {
  readonly k: string;
  readonly v: string;
  readonly small?: string;
}

export interface ReceiptModel {
  /** DCTA-209 (30c): "minus" — a payment a schedule skipped (the key's minus mark: nothing moved). */
  readonly icon: "check" | "hold" | "no" | "minus";
  readonly title: string;
  readonly amount?: string;
  readonly sum: string;
  /** 06: why it waits and that it can be stopped (`.notice.hold`). */
  readonly holdNotice?: string;
  /** A signed step that did not go: the bank's own sentence for it, verbatim (§F.2 18a/18b, 13h–13j). No rule id. */
  readonly refusal?: { readonly text: string };
  readonly kv: readonly KV[];
  readonly deltas: readonly Delta[];
  /** "Nothing left your accounts." — instead of What changed, when nothing did. */
  readonly none?: string;
  readonly verified?: { readonly kind: "verified"; readonly title: string; readonly sub: string };
  readonly cancelHeld?: { readonly bundleId: string; readonly amount: string };
  /** The signed bundle behind it, for the proof (12). */
  readonly bundleId?: string;
  /**
   * DCTA-143: the flagged strings this receipt prints, in the bank's inert form (describe.ts `Inert`) — the sheet sets
   * each apart as data — and the bank's sentence about them with the full texts behind "Show the full text", as on the card.
   */
  readonly inert?: readonly Inert[];
  readonly inertNotices?: readonly InertNotice[];
  /** DCTA-209 (30b, 30c): the schedule a payment came from — its rule and when it was signed; the row opens the schedule. */
  readonly schedule?: { readonly rule: string; readonly sub: string; readonly mandateId: string };
}

const PROXY: Readonly<Record<string, string>> = {
  mobile: "PayNow mobile",
  nric_fin: "PayNow NRIC/FIN",
  uen: "PayNow UEN",
  vpa: "PayNow VPA",
  account_number: "Bank transfer",
};

/** The ledger's rail, in words (a line the assistant did not make). */
const CHANNEL: Readonly<Record<string, string>> = {
  paynow: "PayNow",
  fast: "FAST transfer",
  giro: "GIRO",
  card: "Card payment",
  bill_payment: "Bill payment",
  internal: "Between your accounts",
};

const VERIFIED = { kind: "verified", title: "Verified", sub: "Tap to see how it was checked" } as const;

function moneyOf(step: SignedStep): { value: string; currency: string } | undefined {
  if (step.type === "securities_order") return step.cash_amount;
  if (step.type === "protective_action" || step.type === "payee_add") return undefined;
  return step.amount;
}

/**
 * DCTA-209 F60 (09-scheduled.md 30b, 30c; mock-up `receipt-sent`, `receipt`): a payment a schedule made. Executed (its
 * ledger line): the title as built ("Sent to Mum"), To · From · When, the schedule row, then Verified — the signature that
 * set the schedule up (its signing bundle's proof). Skipped (route 2's payment): the minus mark, "Skipped", to whom and
 * when, Why only when the bank sends `why` (never parsed out of `line`), From, Next, "Nothing left your account.", the
 * schedule row. Every word from the schedule's signed fields and the bank's records.
 */
export function occurrenceReceipt(o: { readonly mandate: MandateView; readonly entry?: LedgerEntry; readonly occurrence?: OccurrenceView }): ReceiptModel {
  const m = o.mandate;
  const schedule = { rule: ruleLine(m.schedule), sub: `A scheduled payment you signed on ${statementDate(m.created_at)}`, mandateId: m.mandate_id };
  const from = mandateFrom(m);
  const e = o.entry;
  if (e !== undefined) {
    const to =
      m.to.kind === "payee"
        ? { k: "To", v: `${m.to.display_name} ${proxyTail(m.to.masked)}`, ...(m.to.nickname === undefined ? {} : { small: m.to.nickname }) }
        : { k: "To", v: `${m.to.display_name} ${lastDigits(m.to.masked)}` };
    return {
      icon: "check",
      title: mandateDoneTitle(m),
      amount: formatMoney(e.amount, e.currency),
      sum: `${shortDate(sgDay(e.posted_at))} · ${m.from.display_name}`,
      kv: [to, from, { k: "When", v: fullWhen(e.posted_at) }],
      deltas: [],
      schedule,
      verified: VERIFIED,
      bundleId: m.bundle_id,
    };
  }
  const occ = o.occurrence;
  const a = paymentAmount(m, occ);
  const figure = a === undefined ? undefined : formatMoney(a.value, a.currency);
  return {
    icon: "minus",
    title: "Skipped",
    ...(figure === undefined ? {} : { amount: figure }),
    sum: `to ${mandateName(m)}${occ === undefined ? "" : ` · ${fullDate(occ.due_on)}`}`,
    kv: [...(occ?.why === undefined ? [] : [{ k: "Why", v: occ.why }]), from, ...(m.next_due_on === undefined ? [] : [{ k: "Next", v: fullDate(m.next_due_on) }])],
    deltas: [],
    none: "Nothing left your account.",
    schedule,
  };
}

export function receiptModel(key: string, ctx: ReceiptContext): ReceiptModel | undefined {
  const sep = key.lastIndexOf(":");
  if (sep > 0) return opReceipt(key.slice(0, sep), key.slice(sep + 1), ctx);
  return lineReceipt(key, ctx);
}

// ------------------------------------------------------------ an operation
function opReceipt(bundleId: string, stepId: string, ctx: ReceiptContext): ReceiptModel | undefined {
  const b = ctx.bundles.get(bundleId);
  const step = b?.view.signing_payload.steps.find((s) => s.step_id === stepId);
  if (b === undefined || step === undefined) return undefined;
  const results = new Map(b.receipt.steps.map((r) => [r.step_id ?? r.index, r]));
  const r = results.get(step.step_id) ?? results.get(step.index);
  const steps = b.view.signing_payload.steps;
  const m = moneyOf(step);
  const amount = m === undefined ? undefined : formatMoney(m.value, m.currency);
  // DCTA-143: the bank's flags ride with the bundle read — a flagged string is printed in its inert form here too.
  const flags = b.view.firewall?.data_flags ?? [];
  const d = describeStep(step, flags);
  const inert = d.inert ?? [];
  const flagged = inert.length === 0 ? {} : { inert, inertNotices: inertNotices(steps, inert) };
  const signedAt = b.receipt.signed_at ?? b.view.signing_payload.created_at;
  const state = r?.state ?? "not_executed";

  // The sum line carries the bundle, so no "Part of" row is needed (§9.3 no. 37).
  const done = doneOf(steps, b.receipt);
  const { ran, held } = done;
  // DCTA-143 × §F.2 18f (ask F19): nothing of the signature went through and nothing waits — each receipt's sum is a
  // single payment's ("Mon 21 Sep, 10:30 · signed once"), never "0 of 2 done".
  const nothingWent = nothingRan(done);
  const bundleSum =
    steps.length === 1 || nothingWent
      ? `${shortWhen(signedAt)} · signed once`
      : ran === steps.length
        ? `${String(steps.length)} things in one go · signed once`
        : `${String(ran)} of ${String(steps.length)} done${held > 0 ? ` · ${String(held)} held` : ""} · signed once`;

  const from = debitOf(step, flags);
  const kv: KV[] = [...whoRows(step, r, flags), ...(from === undefined ? [] : [{ k: "From", v: from }])];
  const reference = step.type === "funds_transfer" && step.reference !== undefined ? shownAs(flags, step.step_id, "reference", step.reference) : undefined;

  if (state === "held") {
    const until = r?.hold_until ?? step.policy.hold_until;
    const reason = heldReason(step);
    const account = sourceShown(step, flags);
    return {
      icon: "hold",
      title: step.type === "funds_transfer" && step.payee !== undefined ? `Held for ${d.name}` : `Held · ${d.title}`,
      ...(amount === undefined ? {} : { amount }),
      sum: `${until === undefined ? "Held" : `Goes out ${shortWhen(until)}`}${account === undefined ? "" : ` · ${account}`}`,
      ...(reason === undefined ? {} : { holdNotice: reason }),
      kv: [...kv, { k: "Signed", v: fullWhen(signedAt) }, ...(reference ? [{ k: "Reference", v: reference }] : [])],
      deltas: [
        {
          dir: "From",
          name: account ?? "",
          ...(step.type === "funds_transfer" && step.payee?.is_new_payee === true ? { text: "New payee" } : {}),
          ...(until === undefined ? {} : { when: `Goes out ${dateTime(until)}` }),
          chip: `Held ${amount ?? ""}`,
          pos: false,
        },
      ],
      verified: VERIFIED,
      ...(amount === undefined ? {} : { cancelHeld: { bundleId, amount } }),
      bundleId,
      ...flagged,
    };
  }

  if (state !== "executed") {
    const cancelled = state.startsWith("cancelled");
    // DCTA-143 × §F.2 18 (ask F17). The step's own sentence from the bank, verbatim, in the receipt's notice: 18a/18b on a
    // step stopped because a step it depended on did not go (B28), 13h–13j on an add (DCTA-126). No rule id. A payment
    // refused when it ran has none (DCTA-126 round 1b): no notice. And 18d: beside a step of the same signature that went
    // or waits, "Nothing left your accounts." would be untrue — the line is this step's own: "The {amount} didn't leave
    // {account}." (no such line for a step that takes from no account).
    const said = r?.customer_message;
    const source = sourceShown(step, flags);
    const none = nothingWent ? NOTHING_LEFT : amount !== undefined && source !== undefined ? `The ${amount} didn't leave ${source}.` : undefined;
    return {
      icon: "no",
      title: cancelled ? "Cancelled" : "Not sent",
      ...(amount === undefined ? {} : { amount }),
      sum: bundleSum,
      ...(said === undefined || said.trim() === "" ? {} : { refusal: { text: said } }),
      kv: [...kv, { k: "Signed", v: fullWhen(signedAt) }, ...(reference ? [{ k: "Reference", v: reference }] : [])],
      deltas: [],
      ...(none === undefined ? {} : { none }),
      verified: VERIFIED,
      bundleId,
      ...flagged,
    };
  }

  // DCTA-85 P1-1: an order that ran says what it PAID everywhere — the headline and every chip —
  // from the bank's `filled_amount`; the signed cap appears once, beside it ("of up to …").
  const paid =
    step.type === "securities_order" && r?.filled_amount !== undefined ? formatMoney(r.filled_amount, step.cash_amount.currency) : amount;
  return {
    icon: "check",
    title: doneTitle(step, d),
    ...(paid === undefined ? {} : { amount: paid }),
    sum: bundleSum,
    kv: [...kv, { k: "When", v: fullWhen(signedAt) }, ...(reference ? [{ k: "Reference", v: reference }] : [])],
    deltas: deltasFor(step, r, ctx, paid ?? "", flags),
    verified: VERIFIED,
    bundleId,
    ...flagged,
  };
}

function doneTitle(step: SignedStep, d: Row): string {
  switch (step.type) {
    case "bill_payment":
      return `Paid ${d.name}`;
    case "funds_transfer":
      return step.payee !== undefined ? `Sent to ${d.name}` : `Moved to ${d.name}`;
    case "securities_order":
      return `${step.side === "sell" ? "Sold" : "Bought"} ${d.name}`;
    case "protective_action":
      return d.title;
    case "payee_add":
      // §F.2 13k's receipt title (no read leads here yet: an add posts nothing to the ledger — backend ask).
      return `Added ${d.name} as a payee`;
  }
}

/** DCTA-143: `flags` — each registry string a row prints is the bank's inert form where it flagged it (`shownAs`). */
function whoRows(step: SignedStep, r: ReceiptStep | undefined, flags: readonly DataFlag[] = []): KV[] {
  const shown = (field: string, value: string): string => shownAs(flags, step.step_id, field, value);
  switch (step.type) {
    case "bill_payment":
      return [
        {
          k: "To",
          v: `${shown("biller/name", step.biller.name)} ${lastDigits(step.biller.masked_account)}`,
          ...(step.bill_reference === undefined ? {} : { small: `Bill reference ${shown("bill_reference", step.bill_reference)}` }),
        },
      ];
    case "funds_transfer": {
      if (step.payee !== undefined) {
        const p = step.payee;
        const rail = PROXY[p.proxy_type] ?? "PayNow";
        const nickname = p.nickname === undefined ? undefined : shown("payee/nickname", p.nickname);
        const who = [p.is_new_payee ? "New payee" : nickname, rail].filter((x): x is string => x !== undefined && x !== "");
        return [{ k: "To", v: `${shown("payee/registered_name", p.registered_name)} ${lastDigits(p.masked_proxy)}`, small: who.join(" · ") }];
      }
      const to = step.to_account;
      return to === undefined ? [] : [{ k: "To", v: `${shown("to_account/display_name", to.display_name)} ${lastDigits(to.masked_number)}` }];
    }
    case "securities_order": {
      const i = step.instrument;
      const filled = r?.units_filled !== undefined && r.fill_price !== undefined;
      const cur = step.cash_amount.currency;
      const cap = formatMoney(step.cash_amount.value, cur);
      // DCTA-83: the fill explained from the step result's own fields — what was paid (of the cap),
      // what stayed in the settlement account, and the rate a cross-currency order ran at.
      const fill: KV[] = [
        ...(r?.filled_amount === undefined ? [] : [{ k: "Paid", v: formatMoney(r.filled_amount, cur), ...(step.amount_is_max === true ? { small: `of up to ${cap}` } : {}) }]),
        ...(r?.cash_unspent === undefined || /^0+\.00$/.test(r.cash_unspent)
          ? []
          : [{ k: "Stayed", v: formatMoney(r.cash_unspent, cur), small: `in ${shown("settlement_account/display_name", step.settlement_account.display_name)}` }]),
        ...(r?.fx_rate === undefined || step.fx === undefined ? [] : [{ k: "Rate", v: `${step.fx.pair} ${r.fx_rate}` }]),
      ];
      return [
        filled
          ? {
              k: "What",
              v: `${i.ticker} · ${shareCount(r.units_filled as string)} at ${formatMoney(r.fill_price as string, i.currency)}`,
              small: "Market order",
            }
          : { k: "What", v: `${i.ticker} · ${i.exchange}`, small: `Market order for ${step.amount_is_max === true ? "up to " : ""}${cap}` },
        ...fill,
      ];
    }
    case "protective_action":
      return [{ k: "On", v: `${shown("target/display_name", step.target.display_name)} ${lastDigits(step.target.masked_number)}` }];
    case "payee_add":
      // As the card named them (describe.ts): the proxy to its end, the card's label.
      return [{ k: "Payee", v: `${shown("payee/registered_name", step.payee.registered_name)} ${proxyTail(step.payee.masked_proxy)}`, small: proxyLabel(step.payee.proxy_type) }];
  }
}

function debitOf(step: SignedStep, flags: readonly DataFlag[] = []): string | undefined {
  const src = debitSource(step);
  return src === undefined ? undefined : `${sourceShown(step, flags) ?? src.account.display_name} ${lastDigits(src.account.masked_number)}`;
}

/** What changed, from the receipt's own before → after per account (ask e). DCTA-143: `flags`, as `whoRows`. */
function deltasFor(step: SignedStep, r: ReceiptStep | undefined, ctx: ReceiptContext, amount: string, flags: readonly DataFlag[] = []): Delta[] {
  const out: Delta[] = [];
  const names = new Map<string, string>(ctx.accounts.map((a) => [a.account_id, a.display_name]));
  for (const c of ctx.cards) if (c.display_name !== undefined) names.set(`card_${c.card_id}`, c.display_name);
  for (const a of accountRefs(step)) names.set(a.account_id, shownAs(flags, step.step_id, a.field, a.display_name));
  const balances: readonly StepBalance[] = r?.balances ?? [];
  const accountRows = balances.filter((b) => b.balance_before !== undefined && b.balance_after !== undefined);
  // The account money left first, then where it went: the order the sheet reads in.
  const sorted = [...accountRows].sort((a, b) => Number(isUp(a)) - Number(isUp(b)));
  for (const b of sorted) {
    const up = isUp(b);
    out.push({
      dir: up ? "To" : "From",
      name: names.get(b.account_id) ?? b.account_id,
      before: formatMoney(b.balance_before as string, b.currency),
      after: formatMoney(b.balance_after as string, b.currency),
      chip: `${up ? "+" : "−"}${amount}`,
      pos: up,
    });
  }
  for (const b of balances.filter((x) => x.owed_before !== undefined && x.owed_after !== undefined)) {
    out.push({
      dir: "To",
      name: names.get(b.account_id) ?? b.account_id,
      pre: "outstanding ",
      before: formatMoney(b.owed_before as string, b.currency),
      after: formatMoney(b.owed_after as string, b.currency),
      chip: `Paid ${amount}`,
      pos: true,
    });
  }
  if (step.type === "funds_transfer" && step.payee !== undefined) {
    const ref = step.reference === undefined || step.reference === "" ? "" : ` · ${shownAs(flags, step.step_id, "reference", step.reference)}`;
    out.push({
      dir: "To",
      name: `${shownAs(flags, step.step_id, "payee/registered_name", step.payee.registered_name)} ${lastDigits(step.payee.masked_proxy)}`,
      text: `${step.payee.proxy_type === "account_number" ? "Transfer" : "PayNow"}${ref}`,
      chip: `Sent ${amount}`,
      pos: true,
    });
  }
  if (step.type === "securities_order" && step.side === "buy" && r?.units_filled !== undefined) {
    out.push({ dir: "To", name: shownAs(flags, step.step_id, "instrument/name", step.instrument.name), text: shareCount(r.units_filled), chip: `+${amount}`, pos: true });
  }
  return out;
}

function isUp(b: StepBalance): boolean {
  return BigInt((b.balance_after as string).replace(".", "")) > BigInt((b.balance_before as string).replace(".", ""));
}

/** The step's own accounts, each with the field its name is flagged under (DCTA-143). */
function accountRefs(step: SignedStep): { account_id: string; display_name: string; field: string }[] {
  switch (step.type) {
    case "funds_transfer":
      return [
        { ...step.debit_account, field: "debit_account/display_name" },
        ...(step.to_account === undefined ? [] : [{ ...step.to_account, field: "to_account/display_name" }]),
      ];
    case "bill_payment":
      return [{ ...step.debit_account, field: "debit_account/display_name" }];
    case "securities_order":
      return [{ ...step.settlement_account, field: "settlement_account/display_name" }];
    case "protective_action":
      return [{ ...step.target, field: "target/display_name" }];
    case "payee_add":
      return [];
  }
}

// ------------------------------------------------------------ a ledger line
function lineReceipt(ref: string, ctx: ReceiptContext): ReceiptModel | undefined {
  const e = ctx.entries.find((x) => x.ledger_ref === ref);
  if (e === undefined) return undefined;
  const account = ctx.accounts.find((a) => a.account_id === e.account_id);
  // A credit card's own line (DCTA-77 ask y) is booked on `card_<card_id>`: named by the card.
  const card = ctx.cards.find((c) => `card_${c.card_id}` === e.account_id);
  const accountName = account?.display_name ?? card?.display_name ?? e.account_id;
  const accountRow =
    account !== undefined
      ? `${account.display_name} ${lastDigits(account.masked_number)}`
      : card !== undefined
        ? `${accountName} ${lastDigits(card.masked_pan)}`
        : accountName;
  const credit = e.direction === "credit";
  const reserved = e.entry_type === "reservation";
  const who = e.counterparty_name ?? "—";
  const rail = e.channel === undefined ? undefined : CHANNEL[e.channel];
  const figure = formatMoney(e.amount, e.currency);
  return {
    icon: reserved ? "hold" : "check",
    title: e.counterparty_name ?? e.reference ?? accountName,
    amount: figure,
    sum: `${shortWhen(e.posted_at)} · ${accountName}`,
    kv: [
      credit ? { k: "From", v: who, ...(rail === undefined ? {} : { small: rail }) } : { k: "To", v: who, ...(rail === undefined ? {} : { small: rail }) },
      credit ? { k: "To", v: accountRow } : { k: "From", v: accountRow },
      { k: "When", v: fullWhen(e.posted_at) },
      ...(e.reference === undefined || e.reference === "" ? [] : [{ k: "Reference", v: e.reference }]),
    ],
    deltas: [
      {
        dir: credit ? "To" : "From",
        name: accountName,
        text: reserved
          ? `Available after ${formatMoney(e.available_after ?? e.balance_after, e.currency)}`
          : e.balance_after.startsWith("-")
            ? // A card account's balance in the book is negative: what the customer owes.
              `Owed after ${formatMoney(e.balance_after.slice(1), e.currency)}`
            : `Balance after ${formatMoney(e.balance_after, e.currency)}`,
        chip: reserved ? `Held ${figure}` : `${credit ? "+" : "−"}${figure}`,
        pos: credit && !reserved,
      },
    ],
  };
}

/** The time a bundle was signed, for a sum line ("21:41"). Exported for the proof's header. */
export function signedClock(receipt: Receipt, view: BundleView): string {
  return clock(receipt.signed_at ?? view.signing_payload.created_at);
}
